import { useState, useEffect, useRef, useCallback } from 'react';
import { WS_URL, WS_CONFIG, ENABLE_LOGS } from '../config';
import { getAuthToken } from '../utils/api';

// Connection states
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
};

// Logger helper
const log = (...args) => {
  if (ENABLE_LOGS) {
    console.log('[WebSocket]', ...args);
  }
};

const logError = (...args) => {
  console.error('[WebSocket]', ...args);
};

/**
 * WebSocket Hook with retry logic and state management
 */
export const useWebSocket = (roomCode, userId) => {
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [messages, setMessages] = useState([]);
  
  // Refs for stable references
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const manuallyClosedRef = useRef(false);
  const connectionTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const isConnectingRef = useRef(false);
  const webrtcHandlerRef = useRef(null);
  
  /**
   * Clear all timeouts
   */
  const clearTimeouts = useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);
  
  /**
   * Calculate reconnect delay with exponential backoff
   */
  const getReconnectDelay = useCallback((attemptNumber) => {
    const delay = WS_CONFIG.RECONNECT_INTERVAL * Math.pow(
      WS_CONFIG.RECONNECT_MULTIPLIER, 
      attemptNumber
    );
    return Math.min(delay, WS_CONFIG.MAX_RECONNECT_DELAY);
  }, []);
  
  /**
   * Setup heartbeat to keep connection alive
   */
  const setupHeartbeat = useCallback((ws) => {
    // Clear existing heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({
            type: 'ping',
            timestamp: Date.now(),
            metadata: {
              roomCode: roomCode,
              userId: userId,
              clientTime: new Date().toISOString(),
            }
          }));
          log('💓 Ping sent');
        } catch (error) {
          logError('❌ Failed to send ping:', error);
        }
      }
    }, WS_CONFIG.HEARTBEAT_INTERVAL);
    
    log('💓 Heartbeat started (interval:', WS_CONFIG.HEARTBEAT_INTERVAL, 'ms)');
  }, []);
  
  /**
   * Close existing connection
   */
  const closeExistingConnection = useCallback((reason = 'New connection requested') => {
    if (wsRef.current) {
      log('🔌 Closing existing connection:', reason);
      
      manuallyClosedRef.current = true;
      clearTimeouts();
      
      // Remove event listeners
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      
      // Close connection
      if (wsRef.current.readyState === WebSocket.CONNECTING || 
          wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, reason);
      }
      
      wsRef.current = null;
    }
  }, [clearTimeouts]);
  
  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    if (isConnectingRef.current) {
      log('⏭️  Connection already in progress');
      return;
    }
    
    if (!roomCode || !userId) {
      logError('❌ Missing roomCode or userId');
      return;
    }
    
    isConnectingRef.current = true;
    closeExistingConnection('Starting new connection');
    
    log('🔌 Connecting to room:', roomCode);
    setConnectionState(ConnectionState.CONNECTING);
    
    // Build WebSocket URL with token auth
    const token = getAuthToken();
    if (!token) {
      logError('❌ Missing auth token');
      setConnectionState(ConnectionState.FAILED);
      isConnectingRef.current = false;
      return;
    }
    const wsUrl = `${WS_URL}/ws/${roomCode}?token=${encodeURIComponent(token)}`;
    log('📡 WebSocket URL:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    
    // Set connection timeout
    connectionTimeoutRef.current = setTimeout(() => {
      logError('❌ Connection timeout');
      setConnectionState(ConnectionState.FAILED);
      isConnectingRef.current = false;
      
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      
      // Attempt reconnect
      if (!manuallyClosedRef.current && 
          reconnectAttemptsRef.current < WS_CONFIG.MAX_RECONNECT_ATTEMPTS) {
        scheduleReconnect();
      }
    }, WS_CONFIG.CONNECTION_TIMEOUT);
    
    // On open
    ws.onopen = () => {
      log('✅ WebSocket connected');
      
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
      
      setConnectionState(ConnectionState.CONNECTED);
      reconnectAttemptsRef.current = 0;
      manuallyClosedRef.current = false;
      isConnectingRef.current = false;
      
      wsRef.current = ws;
      
      // Setup heartbeat
      setupHeartbeat(ws);
    };
    
    // On close
    ws.onclose = (event) => {
      log('🔌 WebSocket closed:', event.code, event.reason);
      
      clearTimeouts();
      wsRef.current = null;
      isConnectingRef.current = false;

      // 4001 = auth failure — don't reconnect, go to re-auth
      if (event.code === 4001) {
        logError('❌ WebSocket auth failed (4001)');
        setConnectionState(ConnectionState.FAILED);
        manuallyClosedRef.current = true;
        return;
      }

      setConnectionState(ConnectionState.DISCONNECTED);
      
      // Determine if should reconnect
      const shouldReconnect = !manuallyClosedRef.current &&
        reconnectAttemptsRef.current < WS_CONFIG.MAX_RECONNECT_ATTEMPTS &&
        event.code !== 1000 && // Normal closure
        event.code !== 1001 && // Going away
        event.code !== 1005;   // No status received
      
      if (shouldReconnect) {
        scheduleReconnect();
      } else if (reconnectAttemptsRef.current >= WS_CONFIG.MAX_RECONNECT_ATTEMPTS) {
        logError('❌ Max reconnection attempts reached');
        setConnectionState(ConnectionState.FAILED);
      }
    };
    
    // On error
    ws.onerror = (error) => {
      logError('❌ WebSocket error:', error);
      
      if (ws.readyState === WebSocket.CONNECTING) {
        setConnectionState(ConnectionState.FAILED);
      } else {
        setConnectionState(ConnectionState.DISCONNECTED);
      }
      
      isConnectingRef.current = false;
    };
    
    // On message
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        log('📩 Message received:', data.type);
        
        // Handle ping-pong
        if (data.type === 'ping') {
          ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
          log('📡 Pong sent');
          return;
        }
        
        if (data.type === 'pong') {
          log('💓 Pong received');
          return;
        }
        
        // Handle WebRTC messages
        if (['offer', 'answer', 'candidate'].includes(data.type)) {
          if (webrtcHandlerRef.current) {
            webrtcHandlerRef.current(data);
          }
          return;
        }
        
        // Store all other messages
        setMessages(prev => [...prev, data]);
        
      } catch (error) {
        logError('❌ Error parsing message:', error);
      }
    };
    
  }, [roomCode, userId, closeExistingConnection, setupHeartbeat]);
  
  /**
   * Schedule reconnection with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (manuallyClosedRef.current) return;
    
    reconnectAttemptsRef.current++;
    const delay = getReconnectDelay(reconnectAttemptsRef.current - 1);
    
    log(`🔄 Scheduling reconnect attempt ${reconnectAttemptsRef.current}/${WS_CONFIG.MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);
    setConnectionState(ConnectionState.RECONNECTING);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      if (!manuallyClosedRef.current) {
        log(`🔄 Reconnecting (attempt ${reconnectAttemptsRef.current})`);
        connect();
      }
    }, delay);
  }, [connect, getReconnectDelay]);
  
  /**
   * Send message via WebSocket
   */
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        log('📤 Message sent:', message.type);
      } catch (error) {
        logError('❌ Error sending message:', error);
      }
    } else {
      logError('❌ Cannot send message: WebSocket not connected');
    }
  }, []);
  
  /**
   * Disconnect manually
   */
  const disconnect = useCallback(() => {
    log('🔌 Manual disconnect');
    manuallyClosedRef.current = true;
    closeExistingConnection('Manual disconnect');
    setConnectionState(ConnectionState.DISCONNECTED);
  }, [closeExistingConnection]);
  
  /**
   * Retry connection manually
   */
  const retryConnection = useCallback(() => {
    log('🔄 Manual retry');
    manuallyClosedRef.current = false;
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);
  
  /**
   * Register WebRTC handler
   */
  const registerWebRTCHandler = useCallback((handler) => {
    webrtcHandlerRef.current = handler;
  }, []);
  
  /**
   * Auto-connect when roomCode and userId are available
   */
  useEffect(() => {
    if (roomCode && userId) {
      connect();
    }
    
    return () => {
      manuallyClosedRef.current = true;
      closeExistingConnection('Component unmounting');
    };
  }, [roomCode, userId, connect, closeExistingConnection, clearTimeouts, scheduleReconnect]);
  
  return {
    connectionState,
    isConnected: connectionState === ConnectionState.CONNECTED,
    messages,
    sendMessage,
    disconnect,
    retryConnection,
    registerWebRTCHandler,
  };
};

export default useWebSocket;
